// EarlyPitchers.jsx
import AppLoader from "../components/AppLoader";
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Fade,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
import { api } from "../services/api";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Navigate } from "react-router-dom"; //useLocation
import AppBackground from "../components/layout/AppBackground";

const EarlyPitchers = () => {

  const { t, i18n } = useTranslation("early_pitchers");
  const { isAuthenticated, user, authLoading } = useAuth();
//  const location = useLocation();

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const [errorFallback, setErrorFallback] = useState("");

  // Para chequear estado de aplicación y mostrar mensajes específicos
  const [applicationStatusLoading, setApplicationStatusLoading] = useState(false);
  const [hasPendingApplication, setHasPendingApplication] = useState(false);
  const [canApply, setCanApply] = useState(true);
  const [statusMessage, setStatusMessage] = useState("");

  const [submitting, setSubmitting] = useState(false); // para el botón submit
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    linkedin: "",
    project_name: "",
    role: "",
    project_website: "",
    project_linkedin: "",
    project_socials: "",
    stage: "",
    accelerator: "",
    fundraising: "",
    why_join: "",
    what_improve: "",
    share_linkedin: true,
    receive_updates: true,
  });

  const sanitizeName = (value) => {
    return value
      .normalize("NFC") // conserva acentos bien formados
      .replace(/[^ \p{L}'-]/gu, ""); // solo letras unicode, espacio, ' y -
  };

  const toTitleCase = (value) => {
    return value
      .toLowerCase()
      .replace(/\p{L}[\p{L}'-]*/gu, (word) =>
        word.charAt(0).toUpperCase() + word.slice(1)
      );
  };

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await api.get("/auth/profile");
        const data = res.data;

        if (res.status === 200) {
          setForm((prev) => ({
            ...prev,
            first_name: `${data.first_name || ""}`.trim(),
            last_name: `${data.last_name || ""}`.trim(),
            email: data.email || "",
          }));
        }
      } catch (err) {
        console.error("Error loading profile", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;

    const fetchApplicationStatus = async () => {
      setApplicationStatusLoading(true);
      try {
        const res = await api.get("/api/early_pitchers/application-status");
        const data = res.data;

        setHasPendingApplication(!!data.has_pending_application);
        setCanApply(!!data.can_apply);

        if (data.is_early_pitcher) {
          setStatusMessage(t("error_already_early_pitcher"));
        } else if (data.has_pending_application) {
          setStatusMessage(t("error_application_exists"));
        } else {
          setStatusMessage("");
        }
      } catch (err) {
        console.error("Error loading early pitchers application status", err);
      } finally {
        setApplicationStatusLoading(false);
      }
    };

    fetchApplicationStatus();
  }, [authLoading, isAuthenticated, t]);

  if (authLoading) {
    return <AppLoader message={t("loading")} />;
  }
  // 🔒 Si no está logueado, lo mandamos al login
  // if (!isAuthenticated) {
  //   return (
  //     <Navigate
  //       to="/login"
  //       replace
  //       state={{ from: location.pathname }} // 👈 ahora pasa "/early-pitchers"
  //     />
  //   );
  // }
  
  // ⭐ Si YA es Early Pitcher, no tiene sentido mostrar esta página → lo mandamos a Billing
  if (user?.is_early_pitcher) {
    return <Navigate to="/billing" replace />;
  }

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };
  const getFirstInvalidField = () => {
    if (!form.first_name.trim()) return "first_name";
    if (!form.last_name.trim()) return "last_name";
    if (!form.email.trim()) return "email";
    if (!form.why_join.trim()) return "why_join";
    if (!form.what_improve.trim()) return "what_improve";
    return null;
    };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === "first_name" || name === "last_name") {
      const cleaned = toTitleCase(sanitizeName(value));
      setForm((prev) => ({ ...prev, [name]: cleaned }));
      return;
    }

    if (name === "email") {
      const cleaned = value.trim().toLowerCase();
      setForm((prev) => ({ ...prev, email: cleaned }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
  setErrorKey("");
  setErrorFallback("");
  e.preventDefault();

  // si ya tiene una aplicación pendiente o es Early Pitcher, no puede aplicar y mostramos mensaje específico
  if (isAuthenticated && !canApply) {
    setErrorKey("error_application_exists");
    return;
  }

  if (!form.email || !isValidEmail(form.email)) {
      setErrorKey("invalid_email");
      const field = document.getElementsByName("email")[0];
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
      field?.focus();
      return;
  }

  const invalidField = getFirstInvalidField();
  if (invalidField) {
    const field = document.querySelector(`[name="${invalidField}"]`);
    if (field) field.focus();
    return;
  }

  // si no está autenticado, validamos que haya contraseña y que coincidan
  if (!isAuthenticated) {
    // Validar mínimo 4 caracteres
    if (form.password.length < 4) {
      setErrorKey("invalid_password");
      return;
    }
    if (form.password !== form.confirm_password) {
      setErrorKey("password_mismatch");
      return;
    }
  }
  setSubmitting(true); // ⬅️ Desactiva botón

  const applicationPayload = {
      linkedin: form.linkedin,
      project_name: form.project_name,
      role: form.role,
      project_website: form.project_website,
      project_linkedin: form.project_linkedin,
      project_socials: form.project_socials,
      stage: form.stage,
      accelerator: form.accelerator,
      fundraising: form.fundraising,
      why_join: form.why_join,
      what_improve: form.what_improve,
      share_linkedin: form.share_linkedin,
      receive_updates: form.receive_updates,
    };

  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const payload = isAuthenticated
    ? applicationPayload
    : {
        ...applicationPayload,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        language_preference: lang,
      };

  try {
    const res = await api.post("/api/early_pitchers/apply",payload);
    if (res.status === 201) {
      setSubmitted(true);
    } else {
      console.error("Unexpected response", res);
    }
  } catch (err) {
      console.error("Submission error:", err);
  console.error("Backend status:", err?.response?.status);
  console.error("Backend data:", err?.response?.data);

  const backendCode = err?.response?.data?.code;
  const backendMessage = err?.response?.data?.message || err?.response?.data?.error;

  const errorMap = {
    EMAIL_ALREADY_EXISTS_LOGIN_REQUIRED: "error_email_exists_login",
    APPLICATION_ALREADY_EXISTS: "error_application_exists",
    ALREADY_EARLY_PITCHER: "error_already_early_pitcher",
    VERIFICATION_EMAIL_ERROR: "error_verification_email",
    VALIDATION_ERROR: "validation_error_generic",
  };

  const mappedKey = errorMap[backendCode];

  if (mappedKey) {
    setErrorKey(mappedKey);
    setErrorFallback("");
  } else if (backendMessage) {
    setErrorKey("");
    setErrorFallback(backendMessage);
  } else {
    setErrorKey("submission_error");
    setErrorFallback("");
  }

  } finally {
    setSubmitting(false); // ⬅️ Reactiva botón
  }
};


  if (loading) return <AppLoader message={t("loading")} />;

  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          px: 0.5,
          pt: 3,
          pb: 10,
        }}
      >
        <Container maxWidth="md">
            <Typography variant="h4" gutterBottom sx={{ color: "white" }}>
                {t("title")}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {t("subtitle")}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
              {t("description")}
            </Typography>

            <Button variant="contained" href="#form" sx={{ px:5, py: 1.2, borderRadius: 999, textTransform: "uppercase" }}>
              {t("cta")}
            </Button>

          {/* WHY SECTION */}
          <Box
            sx={{
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              borderRadius: 4,
              boxShadow: 3,
              maxWidth: "700px",
              py: 4,
              px: {
                xs: 3,
                md: 4
              },
              m: "auto",
              mt: 6,
            }}
          >
            <Typography variant="h5" sx={{ mb: 4, textAlign: "center" }}>
              {t("why_title")}
            </Typography>
              <Typography sx={{ px:1, mt:1, mb:2 }}>{t("benefits")}</Typography>
              <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, mb: 2 }}>
                <CheckIcon
                  sx={{
                    color: "#A855F7",
                    fontSize: "1.6rem",
                    mt: "1px",
                  }}
                />
                <Typography sx={{ ml: 1 }}>
                  {t("benefit1")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, mb: 2 }}>
                <CheckIcon
                  sx={{
                    color: "#A855F7",
                    fontSize: "1.6rem",
                    mt: "1px",
                  }}
                />
                <Typography sx={{ ml: 1 }}>
                  {t("benefit2")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, mb: 2 }}>
                <CheckIcon
                  sx={{
                    color: "#A855F7",
                    fontSize: "1.6rem",
                    mt: "1px",
                  }}
                />
                <Typography sx={{ ml: 1 }}>
                  {t("benefit3")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, mb: 2 }}>
                <CheckIcon
                  sx={{
                    color: "#A855F7",
                    fontSize: "1.6rem",
                    mt: "1px",
                  }}
                />
                <Typography sx={{ ml: 1 }}>
                  {t("benefit4")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", px: 1, mb: 2 }}>
                <CheckIcon
                  sx={{
                    color: "#A855F7",
                    fontSize: "1.6rem",
                    mt: "1px",
                  }}
                />
                <Typography sx={{ ml: 1 }}>
                  {t("benefit5")}
                </Typography>
              </Box>
          </Box>

          {/* FORM */}
          <div id="form" style={{ position: "relative", top: "-40px" }} />
          {isAuthenticated && !applicationStatusLoading && !canApply ? (
            // mensaje de atención si ya tiene aplicación pendiente o es Early Pitcher
            <Box
              sx={{
                backgroundColor: "#212121",
                color: "white",
                borderRadius: 4,
                mt: 6,
                py: 5,
                px: 4,
                maxWidth: "700px",
                mx: "auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                textAlign: "center",
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 42, color: "#f6c215ff", mb: 1 }} />
              <Typography variant="h5" gutterBottomm mb={4}>
                {t("error_title")}
              </Typography>
              <Typography variant="body1">
                {statusMessage}
              </Typography>
            </Box>
          ) : !submitted ? (
            // form
            <Box
                id="form"
                component="form"
                onSubmit={handleSubmit}
                sx={{
                backgroundColor: "#212121",
                color: "white",
                borderRadius: 4,
                mt: 6,
                py: {
                  xs: 4,
                  md: 5
                },
                px: {
                  xs: 4,
                  md: 10
                },
                maxWidth: "700px",
                mx: "auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                }}
            >
                <Typography variant="h5" gutterBottom>
                  {t("form_title")}
                </Typography>

                <Divider
                  sx={{
                    my: 3,
                    borderColor: "rgba(168,85,247,0.4)", // violeta suave
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: "#A855F7", // mismo violeta de la marca
                    fontWeight: 600,
                    mt: 2,
                  }}
                >
                  {t("form_account")}
                </Typography>

                {/* Text Fields */}
                <TextField
                label={t("first_name")}
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  readOnly: isAuthenticated,
                  sx: isAuthenticated
                    ? {
                        backgroundColor: "rgba(255,255,255,0.03)",
                        "& input": {
                          color: "#bbb",
                          WebkitTextFillColor: "#bbb",
                          cursor: "default",
                        },
                      }
                    : {},
                }}
                InputLabelProps={{ sx: { color: isAuthenticated ? "#bbb" : "#eee", } }}
                />
                <TextField
                label={t("last_name")}
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 100 }}
                InputProps={{
                  readOnly: isAuthenticated,
                  sx: isAuthenticated
                    ? {
                        backgroundColor: "rgba(255,255,255,0.03)",
                        "& input": {
                          color: "#bbb",
                          WebkitTextFillColor: "#bbb",
                          cursor: "default",
                        },
                      }
                    : {},
                }}
                InputLabelProps={{ sx: { color: isAuthenticated ? "#bbb" : "#eee", } }}
                />
                <TextField
                  label={t("email")}
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  margin="normal"
                  size="small"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{
                    readOnly: isAuthenticated,
                    sx: isAuthenticated
                      ? {
                          backgroundColor: "rgba(255,255,255,0.03)",
                          "& input": {
                            color: "#bbb",
                            WebkitTextFillColor: "#bbb",
                            cursor: "default",
                          },
                        }
                      : {},
                  }}
                  InputLabelProps={{ sx: { color: isAuthenticated ? "#bbb" : "#eee", } }}
                  // error={!!error && !isValidEmail(form.email)}
                  // helperText={
                  // !!error && !isValidEmail(form.email) ? t("invalid_email") : ""}
                />
                {!isAuthenticated && (
                <>
                  <TextField
                    label={t("password")}
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    inputProps={{ maxLength: 50 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t("confirm_password")}
                    name="confirm_password"
                    autoComplete="new-password"
                    type="password"
                    value={form.confirm_password}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    size="small"
                    inputProps={{ maxLength: 50 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
                )}
                <Divider
                  sx={{
                    my: 3,
                    borderColor: "rgba(168,85,247,0.4)", // violeta suave
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    color: "#A855F7", // mismo violeta de la marca
                    fontWeight: 600,
                    mt: 4,
                  }}
                >
                  {t("form_application")}
                </Typography>

                <TextField
                label={t("linkedin")}
                name="linkedin"
                value={form.linkedin}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("project_name")}
                name="project_name"
                value={form.project_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                {/* Select - Stage */}
                <FormControl fullWidth margin="normal" size="small">
                <InputLabel
                    id="stage-label"
                    sx={{
                    color: "#eee",
                    mb: 0.5, // 👈 este margen evita que el label quede solapado
                    }}
                >
                    {t("stage")}
                </InputLabel>
                <Select
                    labelId="stage-label"
                    name="stage"
                    value={form.stage}
                    onChange={handleChange}
                    label={t("stage")} // 👈 necesario para que el label flote bien
                    displayEmpty
                    sx={{
                    color: "#fff",
                    ".MuiSelect-icon": { color: "#ccc" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                    }}
                    MenuProps={{
                    PaperProps: {
                        sx: {
                        backgroundColor: "#fff",
                        color: "#222",
                        },
                    },
                    }}
                >
                    {t("stage_options", { returnObjects: true }).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <TextField
                label={t("role")}
                name="role"
                value={form.role}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 50 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("project_website")}
                name="project_website"
                value={form.project_website}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("project_linkedin")}
                name="project_linkedin"
                value={form.project_linkedin}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("project_socials")}
                name="project_socials"
                value={form.project_socials}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 300 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("accelerator")}
                name="accelerator"
                value={form.accelerator}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                {/* Select - Fundraising */}
                <FormControl fullWidth margin="normal" size="small">
                <InputLabel
                    id="fundraising-label"
                    sx={{
                    color: "#eee",
                    mb: 0.5, // margen inferior para evitar que se superponga al borde
                    }}
                >
                    {t("fundraising")}
                </InputLabel>
                <Select
                    labelId="fundraising-label"
                    name="fundraising"
                    value={form.fundraising}
                    onChange={handleChange}
                    label={t("fundraising")}
                    displayEmpty
                    sx={{
                    color: "#fff",
                    ".MuiSelect-icon": { color: "#ccc" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#A855F7" },
                    }}
                    MenuProps={{
                    PaperProps: {
                        sx: {
                        backgroundColor: "#fff",
                        color: "#222",
                        },
                    },
                    }}
                >
                    {t("fundraising_options", { returnObjects: true }).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                    </MenuItem>
                    ))}
                </Select>
                </FormControl>

                <TextField
                label={t("why_join")}
                name="why_join"
                value={form.why_join}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                multiline
                minRows={3}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                <TextField
                label={t("what_improve")}
                name="what_improve"
                value={form.what_improve}
                onChange={handleChange}
                fullWidth
                margin="normal"
                size="small"
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                InputLabelProps={{ sx: { color: "#eee" } }}
                />

                {/* Checkboxes */}
                <FormControlLabel
                control={
                    <Checkbox
                    name="share_linkedin"
                    checked={form.share_linkedin}
                    onChange={handleChange}
                    />
                }
                label={t("share_linkedin")}
                sx={{ mt: 1, color: "#eee" }}
                />
                <FormControlLabel
                control={
                    <Checkbox
                    name="receive_updates"
                    checked={form.receive_updates}
                    onChange={handleChange}
                    />
                }
                label={t("receive_updates")}
                sx={{ mt: 1, color: "#eee" }}
                />

                {/* Mensaje de selección limitada */}
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: 3,
                    ml: -1,
                    mr: -1,
                    px: 1.5,
                    py: 2.5,
                    mt: 4,
                    mb: 1,
                    border: "1px solid rgba(255,255,255,0.12)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,                      
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <WarningAmberIcon 
                      sx={{
                        color: "#f6c215ff",
                        fontSize: "1.6rem",
                        mr: 1.5,
                        mt: "2px",
                        opacity: 0.9,
                      }}
                    />
                    <Box>
                      <Typography variant="body1" sx={{ color: "#eee", fontWeight: "bold" }}>
                        {t("attention_title")}
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#A855F7", fontWeight: "bold" }}>
                        {t("attention_message")}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ pl: 5, pr: 1, color: "#eee", lineHeight: 1.45 }}>
                    {t("attention_recommendation")}
                    <br/><br/>
                    {t("attention_closing")}
                    <br/>
                    {t("attention_thanks")}
                    <br/>
                  </Typography>
                </Box>
                
                {/* Error general de submit */}
                {(errorKey || errorFallback) && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errorKey ? t(errorKey) : errorFallback}
                  </Typography>
                )}
                
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ mt: 5, mb: 2, borderRadius: 999, py: 1.2,
                    color: "#fff", // texto normal
                    "&.Mui-disabled": {
                      color: "#fff",            // 👈 texto más claro
                      opacity: 0.6,             // opcional (default suele ser ~0.38)
                      backgroundColor: "#666",  // opcional, para mejor contraste
                    }
                  }}
                  disabled={submitting}
                >
                  {submitting ? t("submitting") : t("submit")}
                </Button>
            </Box>
          ) : (
            // success message
            <Box
                sx={{
                backgroundColor: "#212121",
                color: "white",
                borderRadius: 4,
                py: 6,
                px: 4,
                mt: 6,
                maxWidth: "600px",
                mx: "auto",
                textAlign: "center",
                boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                }}
            >
                <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#4caf50", mb: 1 }} />

                <Typography variant="h5" gutterBottom>
                    {isAuthenticated ? t("thanks_title") : t("thanks_title_guest") }
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                    {isAuthenticated ? t("thanks_message") : t("thanks_message_guest")}
                </Typography>
            </Box>
        )}
        </Container>
      </AppBackground>
    </Fade>
  );
};

export default EarlyPitchers;
